<template>
  <div class="calculator-page">
    <div class="page-header">
      <h1>提前还贷计算器</h1>
      <p class="subtitle">计算提前还款后的每月还款、利息总额及新的还款计划</p>
    </div>
    
    <div class="calculator-container">
      <div class="form-section">
        <h2>基础房贷信息</h2>
        <div class="form-group">
          <label>贷款金额：</label>
          <input 
            type="number" 
            v-model.number="formData.loanAmount" 
            placeholder="请输入贷款金额"
            @input="validateForm"
          />
          <span class="unit">元</span>
        </div>
        
        <div class="form-group">
          <label>贷款期限：</label>
          <select v-model="termType" @change="onTermTypeChange">
            <option value="preset">预设期限</option>
            <option value="custom">自定义期限</option>
          </select>
        </div>
        
        <div class="form-group" v-if="termType === 'preset'">
          <label>选择期限：</label>
          <select v-model="formData.loanTerm" @change="validateForm">
            <option value="">请选择期限</option>
            <option v-for="term in loanTerms" :key="term.value" :value="term.value">
              {{ term.label }}
            </option>
          </select>
        </div>
        
        <div class="form-group" v-if="termType === 'custom'">
          <label>贷款月数：</label>
          <input 
            type="number" 
            v-model.number="formData.loanTerm" 
            placeholder="请输入贷款月数"
            min="1"
            max="600"
            @input="validateForm"
          />
          <span class="unit">月</span>
        </div>
        
        <div class="form-group">
          <label>还款方式：</label>
          <select v-model="formData.repaymentMethod" @change="validateForm">
            <option value="equal_payment">等额本息</option>
            <option value="equal_principal">等额本金</option>
          </select>
        </div>
        
        <div class="form-group">
          <label>贷款利率：</label>
          <input 
            type="number" 
            v-model.number="formData.interestRate" 
            placeholder="请输入年利率"
            step="0.01"
            @input="validateForm"
          />
          <span class="unit">%</span>
        </div>
        
        <div class="form-group">
          <label>贷款时间：</label>
          <div class="date-input-group">
            <input 
              type="number" 
              v-model.number="formData.firstPaymentYear" 
              placeholder="2025"
              min="2020"
              max="2050"
              @input="validateForm"
            />
            <span class="date-unit">年</span>
            <select v-model="formData.firstPaymentMonth" @change="validateForm">
              <option v-for="month in months" :key="month.value" :value="month.value">
                {{ month.label }}
              </option>
            </select>
          </div>
        </div>
      </div>
      
      <div class="form-section">
        <h2>提前还款设置</h2>
        <div class="form-group">
          <label>提前还款日期：</label>
          <div class="date-input-group">
            <input 
              type="number" 
              v-model.number="formData.prepaymentYear" 
              placeholder="2026"
              min="2020"
              max="2050"
              @input="validateForm"
            />
            <span class="date-unit">年</span>
            <select v-model="formData.prepaymentMonth" @change="validateForm">
              <option v-for="month in months" :key="month.value" :value="month.value">
                {{ month.label }}
              </option>
            </select>
          </div>
        </div>
        
        <div class="form-group">
          <label>提前还款方式：</label>
          <select v-model="formData.prepaymentMethod" @change="onPrepaymentMethodChange">
            <option value="full_prepayment">全额提前还款</option>
            <option value="partial_prepayment">部分提前还款</option>
          </select>
        </div>
        
        <div class="form-group" v-if="formData.prepaymentMethod === 'partial_prepayment'">
          <label>提前还款金额：</label>
          <input 
            type="number" 
            v-model.number="formData.prepaymentAmount" 
            placeholder="请输入提前还款金额"
            @input="validateForm"
          />
          <span class="unit">元</span>
        </div>
        
        <div class="form-group" v-if="formData.prepaymentMethod !== 'full_prepayment'">
          <label>新的贷款利率：</label>
          <input 
            type="number" 
            v-model.number="formData.newInterestRate" 
            placeholder="请输入新的年利率"
            step="0.01"
            @input="validateForm"
          />
          <span class="unit">%</span>
        </div>
        
        <div class="form-group" v-if="formData.prepaymentMethod !== 'full_prepayment'">
          <label>新的还款方式：</label>
          <select v-model="formData.newRepaymentMethod" @change="validateForm">
            <option value="equal_payment">等额本息</option>
            <option value="equal_principal">等额本金</option>
          </select>
        </div>
        
        <div class="form-group" v-if="formData.prepaymentMethod !== 'full_prepayment'">
          <label>调整还款方案：</label>
          <select v-model="adjustmentType" @change="onAdjustmentTypeChange">
            <option value="keep_payment">月供基本不变</option>
            <option value="keep_term">还款期限不变</option>
            <option value="adjust_payment">调整月供金额</option>
            <option value="adjust_term">调整还款期限</option>
          </select>
        </div>
        
        <div class="form-group" v-if="adjustmentType === 'adjust_term'">
          <label>新的贷款期限：</label>
          <select v-model="newTermType" @change="onNewTermTypeChange">
            <option value="preset">预设期限</option>
            <option value="custom">自定义期限</option>
          </select>
        </div>
        
        <div class="form-group" v-if="adjustmentType === 'adjust_term' && newTermType === 'preset'">
          <label>选择新期限：</label>
          <select v-model="formData.newLoanTerm" @change="validateForm">
            <option value="">请选择期限</option>
            <option v-for="term in loanTerms" :key="term.value" :value="term.value">
              {{ term.label }}
            </option>
          </select>
        </div>
        
        <div class="form-group" v-if="adjustmentType === 'adjust_term' && newTermType === 'custom'">
          <label>新的贷款月数：</label>
          <input 
            type="number" 
            v-model.number="formData.newLoanTerm" 
            placeholder="请输入新贷款月数"
            min="1"
            max="600"
            @input="validateForm"
          />
          <span class="unit">月</span>
        </div>
        
        <div class="form-group" v-if="adjustmentType === 'adjust_payment'">
          <label>目标月供金额：</label>
          <input 
            type="number" 
            v-model.number="formData.newMonthlyPayment" 
            placeholder="请输入目标月供金额"
            min="0.01"
            step="0.01"
            @input="validateForm"
          />
          <span class="unit">元</span>
        </div>
      </div>
    </div>
    
    <!-- 错误消息显示 -->
    <div v-if="errorMessage" class="error-message">
      <i class="error-icon">⚠️</i>
      {{ errorMessage }}
    </div>
    
    <div class="action-buttons">
      <button 
        class="btn btn-primary" 
        @click="calculateAndNavigate"
        :disabled="!isFormValid"
      >
        计算并查看结果
      </button>
      <button 
        class="btn btn-secondary" 
        @click="addToComparison"
        :disabled="!isFormValid"
      >
        添加到对比方案
      </button>
      <button class="btn btn-outline" @click="resetForm">
        重置表单
      </button>
    </div>
    
    <div class="scheme-list" v-if="savedSchemes.length > 0">
      <h3>已保存的方案</h3>
      <div class="scheme-cards">
        <div 
          v-for="(scheme, index) in savedSchemes" 
          :key="index" 
          class="scheme-card"
        >
          <div class="scheme-header">
            <h4>{{ scheme.name }}</h4>
            <div class="scheme-actions">
              <button class="btn btn-sm btn-info" @click="viewScheme(scheme)">查看详情</button>
              <button class="btn btn-sm" @click="loadScheme(scheme)">加载参数</button>
              <button class="btn btn-sm btn-danger" @click="removeScheme(index)">删除记录</button>
            </div>
          </div>
          
          <div class="scheme-info">
            <div class="info-section">
              <h5>基础信息</h5>
              <div class="info-item">
                <span class="label">贷款金额:</span>
                <span class="value">{{ formatCurrency(scheme.loanAmount) }}元</span>
              </div>
              <div class="info-item">
                <span class="label">贷款期限:</span>
                <span class="value">{{ scheme.loanTerm }}期</span>
              </div>
              <div class="info-item">
                <span class="label">贷款利率:</span>
                <span class="value">{{ scheme.interestRate }}%</span>
              </div>
              <div class="info-item">
                <span class="label">还款方式:</span>
                <span class="value">{{ scheme.repaymentMethod === 'equal_payment' ? '等额本息' : '等额本金' }}</span>
              </div>
            </div>
            
            <div class="info-section">
              <h5>提前还款条件</h5>
              <div class="info-item">
                <span class="label">提前还款日期:</span>
                <span class="value">{{ scheme.prepaymentYear }}年{{ scheme.prepaymentMonth }}月</span>
              </div>
              <div class="info-item">
                <span class="label">提前还款方式:</span>
                <span class="value">{{ scheme.prepaymentMethod === 'full_prepayment' ? '全额提前还款' : '部分提前还款' }}</span>
              </div>
              <div class="info-item" v-if="scheme.prepaymentMethod === 'partial_prepayment'">
                <span class="label">提前还款金额:</span>
                <span class="value">{{ formatCurrency(scheme.prepaymentAmount || 0) }}元</span>
              </div>
              <div class="info-item" v-if="scheme.prepaymentMethod !== 'full_prepayment'">
                <span class="label">调整方案:</span>
                <span class="value">{{ getAdjustmentTypeText(scheme.adjustmentType) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mortgageCalculator } from '../utils/calculator'

export default {
  name: 'Calculator',
  data() {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1
    
    return {
      termType: 'custom',
      newTermType: 'custom',
      adjustmentType: 'keep_payment',
      formData: {
        loanAmount: null,
        loanTerm: null,
        repaymentMethod: 'equal_principal',
        interestRate: null,
        firstPaymentYear: currentYear,
        firstPaymentMonth: currentMonth,
        prepaymentYear: currentYear + 1,
        prepaymentMonth: currentMonth,
        prepaymentMethod: 'partial_prepayment',
        prepaymentAmount: null,
        newInterestRate: null,
        newRepaymentMethod: 'equal_principal',
        newLoanTerm: null,
        newMonthlyPayment: null,
        adjustmentType: 'keep_payment'
      },
      isFormValid: false,
      errorMessage: '',
      savedSchemes: [],
      loanTerms: [
        { value: 12, label: '1年(12期)' },
        { value: 24, label: '2年(24期)' },
        { value: 36, label: '3年(36期)' },
        { value: 48, label: '4年(48期)' },
        { value: 60, label: '5年(60期)' },
        { value: 72, label: '6年(72期)' },
        { value: 84, label: '7年(84期)' },
        { value: 96, label: '8年(96期)' },
        { value: 108, label: '9年(108期)' },
        { value: 120, label: '10年(120期)' },
        { value: 132, label: '11年(132期)' },
        { value: 144, label: '12年(144期)' },
        { value: 156, label: '13年(156期)' },
        { value: 168, label: '14年(168期)' },
        { value: 180, label: '15年(180期)' },
        { value: 192, label: '16年(192期)' },
        { value: 204, label: '17年(204期)' },
        { value: 216, label: '18年(216期)' },
        { value: 228, label: '19年(228期)' },
        { value: 240, label: '20年(240期)' },
        { value: 252, label: '21年(252期)' },
        { value: 264, label: '22年(264期)' },
        { value: 276, label: '23年(276期)' },
        { value: 288, label: '24年(288期)' },
        { value: 300, label: '25年(300期)' },
        { value: 312, label: '26年(312期)' },
        { value: 324, label: '27年(324期)' },
        { value: 336, label: '28年(336期)' },
        { value: 348, label: '29年(348期)' },
        { value: 360, label: '30年(360期)' }
      ],
      months: [
        { value: 1, label: '1月' },
        { value: 2, label: '2月' },
        { value: 3, label: '3月' },
        { value: 4, label: '4月' },
        { value: 5, label: '5月' },
        { value: 6, label: '6月' },
        { value: 7, label: '7月' },
        { value: 8, label: '8月' },
        { value: 9, label: '9月' },
        { value: 10, label: '10月' },
        { value: 11, label: '11月' },
        { value: 12, label: '12月' }
      ]
    }
  },
  watch: {
    formData: {
      handler() {
        this.saveFormData()
      },
      deep: true
    },
    adjustmentType(value) {
      this.formData.adjustmentType = value
      this.saveFormData()
    },
    termType() {
      this.saveFormData()
    },
    newTermType() {
      this.saveFormData()
    }
  },
  mounted() {
    this.loadSavedSchemes()
    this.loadFormData()
    this.handleEditMode()
    this.validateForm()
  },
  methods: {
    onTermTypeChange() {
      if (this.termType === 'preset') {
        // 切换到预设期限时，设置默认值
        this.formData.loanTerm = 360
      } else {
        // 切换到自定义期限时，清空当前值
        this.formData.loanTerm = null
      }
      this.validateForm()
    },
    
    onNewTermTypeChange() {
      if (this.newTermType === 'preset') {
        // 切换到预设期限时，设置默认值
        this.formData.newLoanTerm = 180
      } else {
        // 切换到自定义期限时，清空当前值
        this.formData.newLoanTerm = null
      }
      this.validateForm()
    },
    
    onPrepaymentMethodChange() {
      if (this.formData.prepaymentMethod === 'full_prepayment') {
        this.formData.prepaymentAmount = 0
        this.adjustmentType = 'keep_payment'
        this.formData.adjustmentType = 'keep_payment'
      } else {
        this.formData.prepaymentAmount = null
      }
      this.validateForm()
    },
    
    onAdjustmentTypeChange() {
      // 同步更新formData中的adjustmentType
      this.formData.adjustmentType = this.adjustmentType
      // 调整方案改变时重新验证表单
      this.validateForm()
    },
    
    validateForm() {
      this.errorMessage = ''
      
      let isValid = !!(
        this.formData.loanAmount &&
        this.formData.loanTerm &&
        this.formData.interestRate &&
        this.formData.firstPaymentYear &&
        this.formData.firstPaymentMonth &&
        this.formData.prepaymentYear &&
        this.formData.prepaymentMonth &&
        (this.formData.prepaymentMethod === 'full_prepayment' || this.formData.prepaymentAmount)
      )
      
      // 合同月份是贷款时间，首期还款在下个月；提前还款至少应在下个月
      if (isValid) {
        // 计算月份差
        const monthDiff = (this.formData.prepaymentYear - this.formData.firstPaymentYear) * 12 + 
                         (this.formData.prepaymentMonth - this.formData.firstPaymentMonth)
        
        if (monthDiff < 1) {
          isValid = false
          this.errorMessage = '提前还款日期必须比贷款时间至少晚一个月'
        } else if (monthDiff + 1 > this.formData.loanTerm) {
          isValid = false
          this.errorMessage = '提前还款日期不能超过原贷款期限'
        }
      }
      
      // 如果不是全额提前还款，需要验证相关字段
      if (isValid && this.formData.prepaymentMethod !== 'full_prepayment') {
        isValid = !!(this.formData.newInterestRate && this.formData.newRepaymentMethod)
        
        // 根据调整方案类型验证不同的字段
        if (isValid) {
          if (this.adjustmentType === 'adjust_term') {
            isValid = !!this.formData.newLoanTerm
          } else if (this.adjustmentType === 'adjust_payment') {
            isValid = !!this.formData.newMonthlyPayment
          }
        }
      }
      
      this.isFormValid = isValid
    },
    
    calculateAndNavigate() {
      if (!this.isFormValid) return
      
      try {
        const results = mortgageCalculator.calculate(this.formData)
        this.$router.push({
          name: 'Results',
          params: { results: results }
        })
      } catch (error) {
        alert('计算错误：' + error.message)
        console.error('计算错误：', error)
      }
    },
    
    addToComparison() {
      if (!this.isFormValid) return
      
      const schemeName = prompt('请输入方案名称:', `方案${this.savedSchemes.length + 1}`)
      if (schemeName) {
        const scheme = {
          ...this.formData,
          name: schemeName,
          id: Date.now()
        }
        this.savedSchemes.push(scheme)
        this.saveSchemes()
        this.$message?.success('方案已添加到对比列表')
      }
    },
    
    loadScheme(scheme) {
      // 只加载表单相关的字段，排除results等不需要的字段
      const formFields = {
        loanAmount: scheme.loanAmount,
        loanTerm: scheme.loanTerm,
        repaymentMethod: scheme.repaymentMethod,
        interestRate: scheme.interestRate,
        firstPaymentYear: scheme.firstPaymentYear,
        firstPaymentMonth: scheme.firstPaymentMonth,
        prepaymentYear: scheme.prepaymentYear,
        prepaymentMonth: scheme.prepaymentMonth,
        prepaymentMethod: scheme.prepaymentMethod,
        prepaymentAmount: scheme.prepaymentAmount,
        newInterestRate: scheme.newInterestRate,
        newRepaymentMethod: scheme.newRepaymentMethod,
        newLoanTerm: scheme.newLoanTerm,
        newMonthlyPayment: scheme.newMonthlyPayment,
        adjustmentType: scheme.adjustmentType
      }
      
      this.formData = { ...this.formData, ...formFields }
      
      // 确保adjustmentType同步
      if (scheme.adjustmentType) {
        this.adjustmentType = scheme.adjustmentType
      }
      
      // 根据期限类型设置termType
      if (scheme.loanTerm && this.loanTerms.some(term => term.value === scheme.loanTerm)) {
        this.termType = 'preset'
      } else {
        this.termType = 'custom'
      }
      
      // 根据新期限类型设置newTermType
      if (scheme.newLoanTerm && this.loanTerms.some(term => term.value === scheme.newLoanTerm)) {
        this.newTermType = 'preset'
      } else {
        this.newTermType = 'custom'
      }
      
      this.validateForm()
    },
    
    viewScheme(scheme) {
      try {
        // 使用方案数据计算结果
        const results = mortgageCalculator.calculate(scheme)
        // 跳转到结果页面
        this.$router.push({
          name: 'Results',
          params: { results: results }
        })
      } catch (error) {
        alert('计算错误：' + error.message)
        console.error('计算错误：', error)
      }
    },
    
    removeScheme(index) {
      this.savedSchemes.splice(index, 1)
      this.saveSchemes()
    },
    
    resetForm() {
      this.termType = 'custom'
      this.newTermType = 'custom'
      this.formData = {
        loanAmount: null,
        loanTerm: null,
        repaymentMethod: 'equal_principal',
        interestRate: null,
        firstPaymentYear: new Date().getFullYear(),
        firstPaymentMonth: new Date().getMonth() + 1,
        prepaymentYear: new Date().getFullYear() + 1,
        prepaymentMonth: new Date().getMonth() + 1,
        prepaymentMethod: 'partial_prepayment',
        prepaymentAmount: null,
        newInterestRate: null,
        newRepaymentMethod: 'equal_principal',
        newLoanTerm: null,
        newMonthlyPayment: null,
        adjustmentType: 'keep_payment'
      }
      this.adjustmentType = 'keep_payment'
      this.saveFormData()
      this.validateForm()
    },
    
    saveSchemes() {
      localStorage.setItem('mortgageSchemes', JSON.stringify(this.savedSchemes))
    },
    
    loadSavedSchemes() {
      const saved = localStorage.getItem('mortgageSchemes')
      if (saved) {
        this.savedSchemes = JSON.parse(saved)
      }
    },
    
    saveFormData() {
      localStorage.setItem('mortgageFormData', JSON.stringify(this.formData))
      localStorage.setItem('mortgageAdjustmentType', this.adjustmentType)
      localStorage.setItem('mortgageTermType', this.termType)
      localStorage.setItem('mortgageNewTermType', this.newTermType)
    },
    
    loadFormData() {
      const savedFormData = localStorage.getItem('mortgageFormData')
      const savedAdjustmentType = localStorage.getItem('mortgageAdjustmentType')
      const savedTermType = localStorage.getItem('mortgageTermType')
      const savedNewTermType = localStorage.getItem('mortgageNewTermType')
      
      if (savedFormData) {
        this.formData = { ...this.formData, ...JSON.parse(savedFormData) }
      }
      
      if (savedAdjustmentType) {
        this.adjustmentType = savedAdjustmentType
      }
      
      if (savedTermType) {
        this.termType = savedTermType
      }
      
      if (savedNewTermType) {
        this.newTermType = savedNewTermType
      }
      
      // 注意：不再强制清空自定义期限字段，让用户的数据能够正常回显
    },
    
    handleEditMode() {
      // 检查URL查询参数中是否有编辑ID
      const editId = this.$route.query.edit
      if (editId) {
        // 从localStorage中查找对应的方案
        const savedSchemes = JSON.parse(localStorage.getItem('mortgageSchemes') || '[]')
        const schemeToEdit = savedSchemes.find(scheme => scheme.id == editId)
        
        if (schemeToEdit) {
          // 加载方案数据到表单
          this.loadScheme(schemeToEdit)
        }
      }
    },
    
    
    formatCurrency(amount) {
      if (amount === 0) return '0.00'
      return new Intl.NumberFormat('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount)
    },
    
    getAdjustmentTypeText(adjustmentType) {
      const typeMap = {
        'keep_term': '还款期限不变',
        'keep_payment': '月供基本不变',
        'adjust_term': '调整还款期限',
        'adjust_payment': '调整月供金额'
      }
      return typeMap[adjustmentType] || '未知'
    }
  }
}
</script>

<style scoped>
.calculator-page {
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.page-header h1 {
  font-size: 2.5em;
  margin-bottom: 10px;
  font-weight: 300;
}

.subtitle {
  font-size: 1.1em;
  opacity: 0.9;
}

.calculator-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;
}

.form-section {
  background: white;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.form-section h2 {
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #667eea;
  font-size: 1.3em;
}

.form-group {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-group label {
  min-width: 120px;
  font-weight: 500;
  color: #555;
}

.form-group input,
.form-group select {
  flex: 1;
  padding: 10px;
  border: 2px solid #e1e5e9;
  border-radius: 5px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #667eea;
}

.unit {
  color: #666;
  font-size: 14px;
  min-width: 30px;
}

/* 错误消息样式 */
.error-message {
  background: #fee;
  border: 1px solid #fcc;
  color: #c33;
  padding: 12px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  box-shadow: 0 2px 4px rgba(204, 51, 51, 0.1);
}

.error-icon {
  font-size: 16px;
}

.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: #28a745;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #218838;
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  color: #667eea;
  border: 2px solid #667eea;
}

.btn-outline:hover {
  background: #667eea;
  color: white;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.scheme-list {
  background: white;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.scheme-list h3 {
  color: #333;
  margin-bottom: 20px;
  font-size: 1.3em;
}

.scheme-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  padding: 8px;
}

@media (min-width: 1200px) {
  .scheme-cards {
    grid-template-columns: repeat(3, 1fr);
  }
}

.scheme-card {
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  padding: 0;
  border-radius: 16px;
  border: 1px solid rgba(102, 126, 234, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
}

.scheme-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  border-radius: 16px 16px 0 0;
}

.scheme-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 40px rgba(102, 126, 234, 0.15);
  border-color: rgba(102, 126, 234, 0.3);
}

.scheme-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 20px;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  position: relative;
  text-align: center;
}

.scheme-header::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid #764ba2;
}

.scheme-header h4 {
  color: white;
  margin: 0;
  font-size: 1.1em;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}



.scheme-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.scheme-info {
  display: block;
  padding: 16px;
  padding-top: 20px;
}

.info-section {
  background: rgba(248, 250, 252, 0.6);
  padding: 14px;
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: visible;
  margin-bottom: 12px;
  width: 100%;
}

.info-section:first-child {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
}

.info-section:last-child {
  background: linear-gradient(135deg, rgba(240, 147, 251, 0.05) 0%, rgba(245, 101, 101, 0.05) 100%);
  margin-bottom: 0;
}

.info-section h5 {
  color: #334155;
  margin: 0 0 12px 0;
  font-size: 0.9em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid rgba(102, 126, 234, 0.15);
  padding-bottom: 8px;
}

.info-section:first-child h5::before {
  content: '📊';
  font-size: 1.1em;
}

.info-section:last-child h5 {
  color: #f56565;
}

.info-section:last-child h5::before {
  content: '⚡';
  font-size: 1.1em;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 6px;
  transition: all 0.2s ease;
  min-height: 40px;
  gap: 12px;
  overflow: visible;
  width: 100%;
  box-sizing: border-box;
}

.info-item:hover {
  background: rgba(255, 255, 255, 0.9);
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item .label {
  color: #64748b;
  font-size: 0.85em;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

.info-item .value {
  color: #667eea;
  font-size: 0.9em;
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
  overflow: visible;
  flex-shrink: 0;
  margin-left: auto;
  min-width: fit-content;
  max-width: none;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 14px;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.btn-info {
  background: linear-gradient(135deg, #17a2b8 0%, #20c997 100%);
  color: white;
  border: none;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 8px rgba(23, 162, 184, 0.3);
}

.btn-info:hover {
  background: linear-gradient(135deg, #138496 0%, #1e7e34 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(23, 162, 184, 0.4);
}

.btn-sm {
  padding: 8px 16px;
  font-size: 0.85em;
  border-radius: 20px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-sm:hover {
  transform: translateY(-2px);
}

.scheme-actions .btn-sm {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.scheme-actions .btn-info {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.scheme-actions .btn-info:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.scheme-actions .btn:not(.btn-info):not(.btn-danger) {
  background: rgba(255, 255, 255, 0.9);
  color: #667eea;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.scheme-actions .btn:not(.btn-info):not(.btn-danger):hover {
  background: white;
  color: #5a67d8;
  border-color: rgba(255, 255, 255, 0.5);
}

.scheme-actions .btn-danger {
  background: rgba(245, 101, 101, 0.9);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.scheme-actions .btn-danger:hover {
  background: rgba(229, 62, 62, 0.95);
  border-color: rgba(255, 255, 255, 0.5);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .calculator-page {
    padding: 10px;
  }
  
  .page-header {
    padding: 20px 15px;
    margin-bottom: 20px;
  }
  
  .page-header h1 {
    font-size: 1.8em;
    margin-bottom: 8px;
  }
  
  .subtitle {
    font-size: 1em;
  }
  
  .calculator-container {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .form-section {
    padding: 15px;
  }
  
  .form-section h2 {
    font-size: 1.1em;
    margin-bottom: 15px;
  }
  
  .form-group {
    flex-direction: row;
    align-items: center;
    margin-bottom: 15px;
    gap: 8px;
    flex-wrap: nowrap;
  }
  
  .form-group label {
    min-width: 80px;
    margin-bottom: 0;
    font-size: 14px;
    font-weight: 600;
    flex-shrink: 0;
  }
  
  .form-group input {
    flex: 1;
    min-width: 0;
  }
  
  .form-group .unit {
    position: static;
    transform: none;
    margin-left: 8px;
    color: #666;
    flex-shrink: 0;
    white-space: nowrap;
  }
  
  .date-input-group {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    background: linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%);
    border: 2px solid #e1e8ff;
    border-radius: 10px;
    padding: 10px 12px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    flex-wrap: nowrap;
    outline: none;
  }
  
  .date-input-group:hover {
    border-color: #667eea;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
    transform: translateY(-1px);
  }
  
  .date-input-group:focus-within {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    background: linear-gradient(135deg, #f0f4ff 0%, #e0edff 100%);
  }
  
  .date-input-group input {
    flex: 1;
    min-width: 60px;
    background: transparent;
    border: none;
    outline: none;
    font-size: 14px;
    font-weight: 500;
    color: #2c3e50;
    text-align: center;
    box-shadow: none;
  }
  
  .date-input-group input::placeholder {
    color: #a0aec0;
    font-weight: 400;
  }
  
  .date-input-group select {
    flex: 1;
    min-width: 80px;
    background: transparent;
    border: none;
    outline: none;
    font-size: 14px;
    font-weight: 500;
    color: #2c3e50;
    cursor: pointer;
    text-align: center;
    box-shadow: none;
  }
  
  .date-unit {
    color: #667eea;
    font-size: 16px;
    font-weight: 600;
    white-space: nowrap;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    .date-input-group {
      display: block;
      gap: 12px;
      padding: 16px;
      background: linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%);
      border: 2px solid #e1e8ff;
      border-radius: 12px;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
      outline: none;
    }
    
    .date-input-group:hover {
      border-color: #667eea;
      box-shadow: 0 6px 16px rgba(102, 126, 234, 0.15);
      transform: translateY(-1px);
    }
    
    .date-input-group:focus-within {
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
    
    .date-input-group input {
      width: 100%;
      min-width: auto;
      background: rgba(255, 255, 255, 0.8);
      border: 1px solid rgba(102, 126, 234, 0.2);
      border-radius: 8px;
      padding: 12px 8px;
      font-size: 16px;
      font-weight: 600;
      color: #2c3e50;
      text-align: center;
      transition: all 0.2s ease;
      margin-bottom: 8px;
    }
    
    .date-input-group input:focus {
      background: rgba(255, 255, 255, 0.95);
      border-color: #667eea;
      color: #667eea;
      transform: scale(1.02);
      box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
    }
    
    .date-input-group select {
      width: 100%;
      min-width: auto;
      background: rgba(255, 255, 255, 0.8);
      border: 1px solid rgba(102, 126, 234, 0.2);
      border-radius: 8px;
      padding: 12px 8px;
      font-size: 16px;
      font-weight: 600;
      color: #2c3e50;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      appearance: none;
      background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23667eea' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right 8px center;
      background-size: 16px;
      padding-right: 32px;
    }
    
    .date-input-group select:focus {
      background-color: rgba(255, 255, 255, 0.95);
      border-color: #667eea;
      color: #667eea;
      transform: scale(1.02);
      box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
    }
    
    .date-unit {
      color: #667eea;
      font-size: 18px;
      font-weight: 700;
      white-space: nowrap;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      background: linear-gradient(135deg, #667eea, #764ba2);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      letter-spacing: 0.5px;
    }
  }
  
  
  .form-group input,
  .form-group select {
    width: 100%;
    padding: 12px;
    font-size: 16px; /* 防止iOS缩放 */
    border-radius: 8px;
  }
  
  .unit {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    font-size: 14px;
  }
  
  .form-group {
    position: relative;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    margin-bottom: 20px;
  }
  
  .btn {
    width: 100%;
    padding: 15px;
    font-size: 16px;
    border-radius: 8px;
  }
  
  .scheme-list {
    padding: 15px;
  }
  
  .scheme-list h3 {
    font-size: 1.2em;
    margin-bottom: 15px;
  }
  
  .scheme-cards {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .scheme-card {
    padding: 15px;
  }
  
  .scheme-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 15px;
  }
  
  .scheme-header h4 {
    font-size: 1em;
    margin-bottom: 0;
  }
  
  .scheme-actions {
    align-self: flex-end;
    flex-wrap: wrap;
  }
  
  .scheme-cards {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 4px;
  }
  
  .scheme-card {
    overflow: visible;
  }
  
  .scheme-info {
    display: block;
    padding: 12px;
  }
  
  .info-section {
    padding: 10px;
    margin-bottom: 8px;
    width: 100%;
  }
  
  .info-section:first-child {
    border-radius: 8px;
  }
  
  .info-section:last-child {
    border-radius: 8px;
    margin-bottom: 0;
  }
  
  .info-section h5 {
    font-size: 0.95em;
    margin-bottom: 12px;
  }
  
  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-bottom: 3px;
    padding: 4px 6px;
    min-height: 28px;
    width: 100%;
    box-sizing: border-box;
  }
  
  .info-item .label {
    font-size: 0.7em;
    color: #64748b;
    white-space: nowrap;
    flex-shrink: 0;
  }
  
  .info-item .value {
    font-size: 0.75em;
    font-weight: 700;
    color: #667eea;
    text-align: right;
    white-space: nowrap;
    overflow: visible;
    flex-shrink: 0;
    margin-left: auto;
  }
  
  .scheme-header {
    padding: 16px 20px;
  }
  
  .scheme-header h4 {
    font-size: 1.1em;
  }
  
  .scheme-actions {
    gap: 6px;
  }
  
  .scheme-actions .btn-sm {
    padding: 6px 12px;
    font-size: 0.8em;
  }
  
  .info-item {
    margin-bottom: 6px;
  }
  
  .info-item .label {
    font-size: 13px;
  }
  
  .info-item .value {
    font-size: 14px;
  }
  
  .scheme-results {
    width: 100%;
    padding-top: 10px;
  }
  
  .result-item {
    margin-bottom: 4px;
  }
  
  .result-item .label {
    font-size: 13px;
  }
  
  .result-item .value {
    font-size: 14px;
  }
}

/* 超小屏幕适配 */
@media (max-width: 480px) {
  .calculator-page {
    padding: 5px;
  }
  
  .page-header {
    padding: 15px 10px;
  }
  
  .page-header h1 {
    font-size: 1.5em;
  }
  
  .subtitle {
    font-size: 0.9em;
  }
  
  .form-section {
    padding: 12px;
  }
  
  .form-group input,
  .form-group select {
    padding: 10px;
    font-size: 14px;
  }
  
  .btn {
    padding: 12px;
    font-size: 14px;
  }
  
  .scheme-card {
    padding: 12px;
  }
}
</style>
